import React, { useState } from 'react';
import '../styles/Calendar.css';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  color?: string;
  description?: string;
}

interface CalendarProps {
  events?: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (start: Date, end: Date) => void;
}

type ViewType = 'month' | 'week' | 'day';

const Calendar: React.FC<CalendarProps> = ({
  events = [],
  onEventClick,
  onDateClick,
  onCreateEvent
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('month');

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToPreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];

    // Previous month days
    const prevMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
    const prevMonthDays = prevMonthDate.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthDays - i;
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, day);
      days.push(
        <div key={`prev-${day}`} className="calendar-day other-month" onClick={() => onDateClick?.(date)}>
          <div className="day-number">{day}</div>
          <div className="day-events">
            {getEventsForDate(date).map(event => (
              <div
                key={event.id}
                className="event-chip"
                style={{ backgroundColor: event.color || '#3b82f6' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(event);
                }}
              >
                <span className="event-time-indicator">
                  {new Date(event.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '')}
                </span>
                {' '}{event.title}
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayEvents = getEventsForDate(date);
      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday(date) ? 'today' : ''}`}
          onClick={() => onDateClick?.(date)}
        >
          <div className="day-number">{day}</div>
          <div className="day-events">
            {dayEvents.slice(0, 3).map(event => (
              <div
                key={event.id}
                className="event-chip"
                style={{ backgroundColor: event.color || '#3b82f6' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(event);
                }}
              >
                <span className="event-time-indicator">
                  {new Date(event.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '')}
                </span>
                {' '}{event.title}
              </div>
            ))}
            {dayEvents.length > 3 && (
              <div className="more-events">+{dayEvents.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }

    // Next month days to fill grid
    const totalDays = days.length;
    const remainingDays = 42 - totalDays; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
      days.push(
        <div key={`next-${day}`} className="calendar-day other-month" onClick={() => onDateClick?.(date)}>
          <div className="day-number">{day}</div>
          <div className="day-events">
            {getEventsForDate(date).map(event => (
              <div
                key={event.id}
                className="event-chip"
                style={{ backgroundColor: event.color || '#3b82f6' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(event);
                }}
              >
                <span className="event-time-indicator">
                  {new Date(event.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '')}
                </span>
                {' '}{event.title}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return days;
  };

  const getWeekDates = () => {
    const curr = new Date(currentDate);
    const first = curr.getDate() - curr.getDay();
    const weekDates = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(curr.setDate(first + i));
      weekDates.push(new Date(date));
    }

    return weekDates;
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates();
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="week-view">
        <div className="week-header">
          <div className="time-column-header"></div>
          {weekDates.map((date, index) => (
            <div key={index} className={`week-day-header ${isToday(date) ? 'today' : ''}`}>
              <div className="day-name">{dayNames[date.getDay()]}</div>
              <div className="day-number">{date.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="week-grid">
          <div className="time-column">
            {hours.map(hour => (
              <div key={hour} className="time-slot">
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
            ))}
          </div>
          {weekDates.map((date, dayIndex) => (
            <div key={dayIndex} className="day-column">
              {hours.map(hour => {
                const slotStart = new Date(date);
                slotStart.setHours(hour, 0, 0, 0);
                const slotEnd = new Date(date);
                slotEnd.setHours(hour + 1, 0, 0, 0);

                const slotEvents = events.filter(event => {
                  const eventStart = new Date(event.start);
                  return eventStart >= slotStart && eventStart < slotEnd;
                });

                return (
                  <div
                    key={hour}
                    className="time-slot"
                    onClick={() => onCreateEvent?.(slotStart, slotEnd)}
                  >
                    {slotEvents.map(event => (
                      <div
                        key={event.id}
                        className="week-event"
                        style={{ backgroundColor: event.color || '#3b82f6' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventClick?.(event);
                        }}
                      >
                        {event.title}
                        <div className="event-time-range" style={{ fontSize: '10px', opacity: 0.9, marginTop: '2px' }}>
                          {new Date(event.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} -
                          {new Date(event.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);

    return (
      <div className="day-view">
        <div className="day-view-header">
          <div className="day-name">{dayNames[currentDate.getDay()]}</div>
          <div className="day-date">{currentDate.getDate()}</div>
        </div>
        <div className="day-grid">
          <div className="time-column">
            {hours.map(hour => (
              <div key={hour} className="time-slot">
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
            ))}
          </div>
          <div className="events-column">
            {hours.map(hour => {
              const slotStart = new Date(currentDate);
              slotStart.setHours(hour, 0, 0, 0);
              const slotEnd = new Date(currentDate);
              slotEnd.setHours(hour + 1, 0, 0, 0);

              const slotEvents = events.filter(event => {
                const eventStart = new Date(event.start);
                return eventStart >= slotStart && eventStart < slotEnd;
              });

              return (
                <div
                  key={hour}
                  className="time-slot"
                  onClick={() => onCreateEvent?.(slotStart, slotEnd)}
                >
                  {slotEvents.map(event => (
                    <div
                      key={event.id}
                      className="day-event"
                      style={{ backgroundColor: event.color || '#3b82f6' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick?.(event);
                      }}
                    >
                      <div className="event-title">{event.title}</div>
                      <div className="event-time">
                        {new Date(event.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const handlePrevious = () => {
    if (view === 'month') goToPreviousMonth();
    else if (view === 'week') goToPreviousWeek();
    else goToPreviousDay();
  };

  const handleNext = () => {
    if (view === 'month') goToNextMonth();
    else if (view === 'week') goToNextWeek();
    else goToNextDay();
  };

  const getHeaderTitle = () => {
    if (view === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (view === 'week') {
      const weekDates = getWeekDates();
      const start = weekDates[0];
      const end = weekDates[6];
      if (start.getMonth() === end.getMonth()) {
        return `${monthNames[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      } else {
        return `${monthNames[start.getMonth()]} ${start.getDate()} - ${monthNames[end.getMonth()]} ${end.getDate()}, ${start.getFullYear()}`;
      }
    } else {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
    }
  };

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button className="today-btn" onClick={goToToday}>Today</button>
          <button className="nav-btn" onClick={handlePrevious}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button className="nav-btn" onClick={handleNext}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
          <h2 className="calendar-title">{getHeaderTitle()}</h2>
        </div>
        <div className="view-switcher">
          <button
            className={`view-btn ${view === 'day' ? 'active' : ''}`}
            onClick={() => setView('day')}
          >
            Day
          </button>
          <button
            className={`view-btn ${view === 'week' ? 'active' : ''}`}
            onClick={() => setView('week')}
          >
            Week
          </button>
          <button
            className={`view-btn ${view === 'month' ? 'active' : ''}`}
            onClick={() => setView('month')}
          >
            Month
          </button>
        </div>
      </div>

      {view === 'month' && (
        <div className="month-view">
          <div className="calendar-weekdays">
            {dayNames.map(day => (
              <div key={day} className="weekday-name">{day}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {renderMonthView()}
          </div>
        </div>
      )}

      {view === 'week' && renderWeekView()}
      {view === 'day' && renderDayView()}
    </div>
  );
};

export default Calendar;
